import React, { useState, useRef, useEffect, useMemo } from 'react'
import Flatpickr from 'react-flatpickr'
import { Portuguese } from 'flatpickr/dist/l10n/pt'
import 'flatpickr/dist/themes/dark.css'
import { FP_CALENDAR_THEME, markOtherBound } from '../../../utils/styles/flatpickrTheme'
import Select, { GroupBase } from 'react-select'
import { AlertTriangle, ArrowUpDown, Trash2 } from 'lucide-react'
import { Categoria } from './hooks/useVarList'
import { isMultiPanelChart } from './hooks/useChartData'
import { Sugestao } from './hooks/useSugestoes'

type VarOpt = { value: string; label: string }

const PERIOD_PRESETS = [
    { label: '1h',  ms: 1  * 60 * 60 * 1000 },
    { label: '6h',  ms: 6  * 60 * 60 * 1000 },
    { label: '12h', ms: 12 * 60 * 60 * 1000 },
    { label: '24h', ms: 24 * 60 * 60 * 1000 },
    { label: '3d',  ms: 3  * 24 * 60 * 60 * 1000 },
    { label: '7d',  ms: 7  * 24 * 60 * 60 * 1000 },
    { label: '15d', ms: 15 * 24 * 60 * 60 * 1000 },
    { label: '30d', ms: 30 * 24 * 60 * 60 * 1000 },
]

const PREDEFINED = [
    { id: 'autoteste',          label: 'Autoteste das Bombas' },
    { id: 'temperatura-oleo',   label: 'Temperatura do Óleo' },
    { id: 'umidade-oleo',       label: 'Umidade do Óleo' },
    { id: 'pressao-filtro',     label: 'Pressão do Filtro' },
    { id: 'ternos',             label: 'Pressão e Deslocamento dos Ternos' },
    { id: 'tempo-nivelamento',  label: 'Diferença de Deslocamento' },
    { id: 'terno-critico',      label: 'Ocorrências de Terno Crítico' },
    { id: 'media-status-terno', label: 'Média de Status por Terno' },
]

// Sub-itens dos gráficos multi-painel
function getSubItems(chartId: string, categorias: Categoria[], activeTernos?: number[]) {
    const qtdTernos = categorias.filter(c => c.nome.toLowerCase().includes('terno')).length || 3
    const qtdBombas = (categorias.find(c => c.nome === 'Unidade Hidráulica')?.variaveis ?? [])
        .filter(v => v.campo.includes('resultado_teste_bomba')).length || 2

    if (chartId === 'autoteste') {
        return Array.from({ length: qtdBombas }, (_, i) => ({
            id: `bomba_${i + 1}`,
            label: `Bomba ${i + 1}`,
        }))
    }
    const hasTernos = ['ternos', 'media-status-terno', 'terno-critico', 'tempo-nivelamento']
    if (hasTernos.includes(chartId)) {
        // activeTernos vem de Moenda.tsx (já filtrado pelo backend) — usado em vez da contagem de
        // categorias "Terno N", que /graficos/variaveis gera para todo terno cadastrado no banco,
        // mesmo os sem o 4.0 instalado. Preserva os IDs reais (cobre o caso de gaps, ex: 1 e 3 sem o 2).
        const ternoIds = activeTernos && activeTernos.length > 0
            ? activeTernos
            : Array.from({ length: qtdTernos }, (_, i) => i + 1)
        return ternoIds.map(n => ({
            id: String(n),
            label: `Terno ${n}`,
        }))
    }
    return []
}

function subPanelExists(chartId: string, subId: string, existingChartIds: string[]) {
    // autoteste: subId vem como 'bomba_N', id real do painel é 'teste_bomba_N' (ver useChartData)
    if (chartId === 'autoteste')     return existingChartIds.includes(`autoteste:teste_${subId}`)
    if (chartId === 'ternos')        return existingChartIds.includes(`ternos:t${subId}:pressao`)
    return existingChartIds.includes(`${chartId}:t${subId}`)
}

function subPanelLoading(chartId: string, subId: string, loadingChartIds: string[]) {
    if (chartId === 'autoteste')     return loadingChartIds.includes(`autoteste:teste_${subId}`)
    if (chartId === 'ternos')        return loadingChartIds.includes(`ternos:t${subId}:pressao`)
    return loadingChartIds.includes(`${chartId}:t${subId}`)
}

function sugestaoLoading(s: Sugestao, loadingChartIds: string[], pendingAddAllIds: string[]) {
    if (s.campo) return loadingChartIds.includes(`variavel:${s.campo}`)
    if (s.onlyId) return subPanelLoading(s.grafico, s.onlyId, loadingChartIds)
    return isMultiPanelChart(s.grafico) ? pendingAddAllIds.includes(s.grafico) : loadingChartIds.includes(s.grafico)
}

const CARD: React.CSSProperties = {
    background: 'rgba(94,94,94,0.1)',
    backdropFilter: 'blur(4px)',
    borderRadius: 20,
    padding: '14px 16px',
    marginBottom: 10,
    boxSizing: 'border-box',
}

const SECTION_TITLE: React.CSSProperties = {
    fontFamily: 'Montserrat, sans-serif',
    fontSize: 12,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.8)',
    marginBottom: 8,
    letterSpacing: 0.5,
    textTransform: 'uppercase' as const,
}

const SELECT_STYLE: React.CSSProperties = {
    width: '100%',
    padding: '6px 10px',
    borderRadius: 8,
    background: 'rgba(255,255,255,0.08)',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.15)',
    fontSize: 12,
    marginBottom: 8,
    boxSizing: 'border-box' as const,
}

const APPLY_BTN: React.CSSProperties = {
    width: '100%',
    padding: '9px 0',
    borderRadius: 10,
    background: 'linear-gradient(90deg,#5292D1,#92B258)',
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'Montserrat, sans-serif',
    fontWeight: 700,
    fontSize: 13,
    marginTop: 10,
}

/* Flatpickr overrides — dark theme ajustado para o design */
const FP_STYLE = `
.dhm-fp-wrap .flatpickr-input {
    width: 100%;
    box-sizing: border-box;
    background: rgba(255,255,255,0.07);
    border: 1px solid rgba(255,255,255,0.15);
    border-radius: 10px;
    color: #fff;
    font-family: 'Nunito', sans-serif;
    font-size: 13px;
    padding: 9px 12px;
    outline: none;
    cursor: pointer;
    transition: border-color 0.15s;
}
.dhm-fp-wrap .flatpickr-input:focus,
.dhm-fp-wrap .flatpickr-input.active {
    border-color: #5292D1;
}
@media (max-width: 600px) {
    .dhm-fp-wrap .flatpickr-input { font-size: 16px; }
}
${FP_CALENDAR_THEME}
`

// Configuração dos chips de estatística
const STAT_CHIPS = [
    { key: 'mean',   label: 'Média',     symbol: 'μ',   color: '#5292D1' },
    { key: 'median', label: 'Mediana',   symbol: 'Med', color: '#92B258' },
    { key: 'mode',   label: 'Moda',      symbol: 'Mo',  color: '#E8A84C' },
    { key: 'max',    label: 'Máxima',    symbol: 'Max', color: '#7BB8E8' },
    { key: 'min',    label: 'Mínima',    symbol: 'Min', color: '#7BB8E8' },
    { key: 'stdDev', label: 'Desvio σ',  symbol: 'σ',   color: 'rgba(255,255,255,0.55)' },
] as const

const SCALE_INPUT: React.CSSProperties = {
    flex: 1,
    padding: '5px 8px',
    borderRadius: 7,
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.12)',
    color: '#fff',
    fontSize: 16,
    fontFamily: 'Nunito,sans-serif',
    outline: 'none',
    width: 0,
    boxSizing: 'border-box' as const,
}

interface Props {
    token: string
    categorias: Categoria[]
    activeTernos?: number[]
    inicio: Date
    fim: Date
    onDateChange: (inicio: Date, fim: Date) => void
    onPeriodoPendingChange?: (pending: boolean) => void
    isMobile?: boolean
    onReorganize?: () => void
    onClearCharts?: () => void
    sugestoes: Sugestao[]
    onSelectSugestao: (s: Sugestao) => void
    onAddChart:    (grafico: string, label: string, campo?: string, onlyId?: string) => void
    onRemoveChart: (grafico: string, campo?: string, onlyId?: string) => void
    existingChartIds: string[]
    loadingChartIds: string[]
    pendingAddAllIds: string[]
    chartLabels: Record<string, string>
    enabledStats: Set<string>
    onToggleStat: (key: string) => void
    axisMode: 'amplo' | 'moderado' | 'preciso' | 'manual'
    onAxisModeChange: (mode: 'amplo' | 'moderado' | 'preciso' | 'manual') => void
    axisConfig: Record<string, { min: string; max: string }>
    onAxisConfigChange: (chartId: string, field: 'min' | 'max', value: string) => void
}

// Estilos do react-select para fundo escuro
const SELECT_FONT = 'Nunito, sans-serif'
const darkSelectStyles = {
    control: (b: any, s: any) => ({ ...b, background: 'rgba(255,255,255,0.07)', borderColor: s.isFocused ? '#5292D1' : 'rgba(255,255,255,0.15)', borderRadius: 8, fontSize: 12, fontFamily: SELECT_FONT, boxShadow: 'none', minHeight: 34, cursor: 'pointer', '&:hover': { borderColor: '#5292D1' } }),
    menu: (b: any) => ({ ...b, background: '#1e2b3a', borderRadius: 10, border: '1px solid rgba(255,255,255,0.12)', zIndex: 9999 }),
    menuPortal: (b: any) => ({ ...b, zIndex: 9999 }),
    option: (b: any, s: any) => ({ ...b, background: s.isSelected ? '#5292D1' : s.isFocused ? 'rgba(82,146,209,0.2)' : 'transparent', color: s.isSelected ? '#fff' : 'rgba(255,255,255,0.8)', fontSize: 12, fontFamily: SELECT_FONT, cursor: 'pointer' }),
    groupHeading: (b: any) => ({ ...b, color: '#5292D1', fontSize: 10, fontFamily: SELECT_FONT, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' as const }),
    singleValue: (b: any) => ({ ...b, color: '#fff', fontSize: 12, fontFamily: SELECT_FONT }),
    placeholder: (b: any) => ({ ...b, color: 'rgba(255,255,255,0.3)', fontSize: 12, fontFamily: SELECT_FONT }),
    input: (b: any) => ({ ...b, color: '#fff', fontSize: 16, fontFamily: SELECT_FONT }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (b: any) => ({ ...b, color: 'rgba(255,255,255,0.3)', padding: '0 6px' }),
    clearIndicator: (b: any) => ({ ...b, color: 'rgba(255,255,255,0.3)' }),
}

const Sidebar: React.FC<Props> = ({ token, categorias, activeTernos, inicio, fim, onDateChange, onPeriodoPendingChange, isMobile, onReorganize, onClearCharts, sugestoes, onSelectSugestao, onAddChart, onRemoveChart, existingChartIds, loadingChartIds, pendingAddAllIds, chartLabels, enabledStats, onToggleStat, axisMode, onAxisModeChange, axisConfig, onAxisConfigChange }) => {
    const [varCampo, setVarCampo] = useState('')
    const [localInicio, setLocalInicio] = useState(inicio)
    const [localFim, setLocalFim] = useState(fim)

    // Avisa o componente pai sempre que o período escolhido ainda não foi aplicado
    useEffect(() => {
        onPeriodoPendingChange?.(localInicio.getTime() !== inicio.getTime() || localFim.getTime() !== fim.getTime())
    }, [localInicio, localFim, inicio, fim, onPeriodoPendingChange])
    const [expandedChart, setExpandedChart] = useState<string | null>(null)

    // Refs das instâncias Flatpickr — sem value controlado para evitar flash da string UTC
    const fpInicioRef = useRef<any>(null)
    const fpFimRef    = useRef<any>(null)

    // Memoizado: react-flatpickr recria a instância (fechando o calendário) toda vez que a
    // referência de `options` muda — por isso só muda quando o limite do OUTRO campo muda
    // (necessário para o destaque dhm-fp-other-bound), não a cada re-render do componente.
    const inicioFpOptions = useMemo(() => ({
        defaultDate: localInicio,
        enableTime: true,
        time_24hr: true,
        dateFormat: 'd/m/Y H:i',
        locale: Portuguese,
        allowInput: false,
        appendTo: document.body,
        disableMobile: true,
        onDayCreate: markOtherBound(localFim),
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [localFim])

    const fimFpOptions = useMemo(() => ({
        defaultDate: localFim,
        enableTime: true,
        time_24hr: true,
        dateFormat: 'd/m/Y H:i',
        locale: Portuguese,
        allowInput: false,
        appendTo: document.body,
        disableMobile: true,
        onDayCreate: markOtherBound(localInicio),
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [localInicio])

    // Predefinição de período: aplica direto (sem precisar do botão "Aplicar período") e
    // sincroniza os inputs Flatpickr via ref, já que eles são uncontrolled (defaultDate só
    // vale na primeira renderização).
    const applyPreset = (ms: number) => {
        const novoFim = new Date()
        const novoInicio = new Date(novoFim.getTime() - ms)
        setLocalInicio(novoInicio)
        setLocalFim(novoFim)
        fpInicioRef.current?.flatpickr?.setDate(novoInicio)
        fpFimRef.current?.flatpickr?.setDate(novoFim)
        onDateChange(novoInicio, novoFim)
    }

    // react-select: opções agrupadas por categoria
    const varOptions: GroupBase<VarOpt>[] = categorias.map(cat => ({
        label: cat.nome,
        options: cat.variaveis.map(v => ({ value: v.campo, label: v.label }))
    }))
    const selectedVar = varOptions.flatMap(g => (g as any).options as VarOpt[]).find(o => o.value === varCampo) ?? null
    // Verifica se já adicionado como variável personalizada OU como gráfico predefinido com o mesmo ID
    const varAdded = varCampo ? (
        existingChartIds.includes(`variavel:${varCampo}`) ||
        existingChartIds.includes(varCampo)
    ) : false

    const handleVarAction = () => {
        if (!varCampo) return
        const label = selectedVar?.label ?? varCampo
        if (varAdded) {
            onRemoveChart('variavel', varCampo)
            setVarCampo('')
        } else {
            onAddChart('variavel', label, varCampo)
            setVarCampo('')
        }
    }

    return (
        <div style={{ overflowY: 'auto', overflowX: 'hidden', height: '100%', paddingRight: 2, boxSizing: 'border-box' }}>
            <style>{FP_STYLE}</style>

            {/* ── Gráficos pré-definidos ── */}
            {isMobile && (onReorganize || onClearCharts) && (
                <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                    {onReorganize && (
                        <button onClick={onReorganize} title="Reorganizar por terno/bomba"
                            style={{ display: 'flex', flex: 1, height: 35, justifyContent: 'center', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.07)', border: 'none', borderRadius: 8, padding: '6px 10px', color: 'rgba(255,255,255,0.6)', fontFamily: 'Nunito,sans-serif', fontSize: 11, cursor: 'pointer' }}>
                            <ArrowUpDown size={13} strokeWidth={1.8} />
                            Reordenar
                        </button>
                    )}
                    {onClearCharts && (
                        <button onClick={onClearCharts} disabled={existingChartIds.length === 0} title="Limpar gráficos"
                            style={{
                                display: 'flex', flex: 1, height: 35, justifyContent: 'center', alignItems: 'center', gap: 5,
                                background: 'rgba(255,255,255,0.07)', border: 'none', borderRadius: 8, padding: '6px 10px',
                                color: 'rgba(255,255,255,0.6)', fontFamily: 'Nunito,sans-serif', fontSize: 11,
                                cursor: existingChartIds.length === 0 ? 'default' : 'pointer',
                                opacity: existingChartIds.length === 0 ? 0.4 : 1,
                            }}>
                            <Trash2 size={13} strokeWidth={1.8} />
                            Limpar
                        </button>
                    )}
                </div>
            )}

            <div style={CARD}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={SECTION_TITLE}>Gráficos pré-definidos</div>
                    
                </div>
                {PREDEFINED.map(p => {
                    const isMulti = isMultiPanelChart(p.id)
                    const allAdded = isMulti
                        ? existingChartIds.some(id => id.startsWith(p.id + ':'))
                        : existingChartIds.includes(p.id)
                    const isLoading = isMulti
                        ? pendingAddAllIds.includes(p.id)
                        : loadingChartIds.includes(p.id)
                    const isExpanded = expandedChart === p.id
                    const subItems = isMulti ? getSubItems(p.id, categorias, activeTernos) : []

                    // Cor de fundo baseada no estado — só background muda, borda sempre neutra
                    const bg = allAdded
                        ? 'rgba(146,178,88,0.08)'
                        : isExpanded
                            ? 'rgba(82,146,209,0.1)'
                            : 'transparent'
                    const textColor = allAdded
                        ? '#92B258'
                        : isExpanded
                            ? '#5292D1'
                            : 'rgba(255,255,255,0.7)'

                    return (
                        <div key={p.id} style={{ marginBottom: 2 }}>
                            {/* Botão principal */}
                            <button
                                disabled={isLoading}
                                onClick={() => {
                                    if (isLoading) return
                                    if (isMulti) {
                                        if (allAdded && !isExpanded) { onRemoveChart(p.id); return }
                                        setExpandedChart(isExpanded ? null : p.id)
                                    } else {
                                        allAdded ? onRemoveChart(p.id) : onAddChart(p.id, p.label)
                                    }
                                }}
                                style={{
                                    width: '100%', textAlign: 'left', padding: '7px 10px',
                                    borderRadius: isExpanded ? '8px 8px 0 0' : 8,
                                    background: bg,
                                    border: 'none',
                                    color: textColor,
                                    fontSize: 12, fontFamily: 'Nunito, sans-serif',
                                    cursor: isLoading ? 'default' : 'pointer',
                                    opacity: isLoading ? 0.6 : 1,
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    transition: 'background 0.15s',
                                }}
                                onMouseEnter={e => { if (!allAdded && !isExpanded && !isLoading) e.currentTarget.style.background = 'rgba(255,255,255,0.06)' }}
                                onMouseLeave={e => { if (!allAdded && !isExpanded && !isLoading) e.currentTarget.style.background = 'transparent' }}
                            >
                                <span>{p.label}</span>
                                <span style={{ fontSize: 12, opacity: 0.7, marginLeft: 4 }}>
                                    {isLoading ? '…' : isMulti ? (isExpanded ? '▲' : '▼') : (allAdded ? '✓' : '+')}
                                </span>
                            </button>

                            {/* Sub-itens expandidos */}
                            {isExpanded && subItems.length > 0 && (
                                <div style={{
                                    background: 'rgba(82,146,209,0.06)',
                                    borderRadius: '0 0 8px 8px',
                                    padding: '6px 10px 8px',
                                    display: 'flex', flexWrap: 'wrap', gap: 5,
                                }}>
                                    <button
                                        disabled={pendingAddAllIds.includes(p.id)}
                                        onClick={() => onAddChart(p.id, p.label)}
                                        style={{
                                            padding: '3px 10px', borderRadius: 20, fontSize: 11,
                                            fontFamily: 'Nunito,sans-serif', border: 'none',
                                            cursor: pendingAddAllIds.includes(p.id) ? 'default' : 'pointer',
                                            opacity: pendingAddAllIds.includes(p.id) ? 0.5 : 1,
                                            background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)',
                                        }}
                                    >{pendingAddAllIds.includes(p.id) ? '… Carregando' : '+ Todos'}</button>
                                    {subItems.map(sub => {
                                        const subAdded = subPanelExists(p.id, sub.id, existingChartIds)
                                        const subLoading = subPanelLoading(p.id, sub.id, loadingChartIds)
                                        return (
                                            <button
                                                key={sub.id}
                                                disabled={subLoading}
                                                onClick={() => {
                                                    if (subLoading) return
                                                    subAdded
                                                        ? onRemoveChart(p.id, undefined, sub.id)
                                                        : onAddChart(p.id, sub.label, undefined, sub.id)
                                                }}
                                                style={{
                                                    padding: '3px 10px', borderRadius: 20, fontSize: 11,
                                                    fontFamily: 'Nunito,sans-serif', border: 'none',
                                                    cursor: subLoading ? 'default' : 'pointer',
                                                    opacity: subLoading ? 0.6 : 1,
                                                    background: subAdded ? 'rgba(146,178,88,0.2)' : 'rgba(82,146,209,0.2)',
                                                    color: subAdded ? '#92B258' : '#5292D1',
                                                }}
                                            >
                                                {subLoading ? '… ' : subAdded ? '✕ ' : '+'} {sub.label}
                                            </button>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>

            {/* ── Gráficos sugeridos ── */}
            {sugestoes.length > 0 && (
                <div style={CARD}>
                    <div style={SECTION_TITLE}>Gráficos sugeridos</div>
                    {sugestoes.map(s => {
                        const isLoading = sugestaoLoading(s, loadingChartIds, pendingAddAllIds)
                        return (
                            <button key={s.id} disabled={isLoading} onClick={() => { if (!isLoading) onSelectSugestao(s) }}
                                style={{
                                    width: '100%', textAlign: 'left', padding: '7px 10px', marginBottom: 2,
                                    borderRadius: 8, border: 'none', cursor: isLoading ? 'default' : 'pointer',
                                    opacity: isLoading ? 0.6 : 1,
                                    background: s.severidade === 'critico' ? 'rgba(240,63,63,0.1)' : 'rgba(229,211,74,0.1)',
                                    display: 'flex', alignItems: 'center', gap: 6,
                                }}>
                                <AlertTriangle size={13} color={s.severidade === 'critico' ? '#F03F3F' : '#E5D34A'} strokeWidth={2} style={{ flexShrink: 0 }} />
                                <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: 12, color: '#fff', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.descricao}>
                                    {s.titulo}
                                </span>
                                <span style={{ fontSize: 12, opacity: 0.7, flexShrink: 0 }}>{isLoading ? '…' : '+'}</span>
                            </button>
                        )
                    })}
                </div>
            )}

            {/* ── Variável personalizada ── */}
            <div style={CARD}>
                <div style={SECTION_TITLE}>Variável personalizada</div>
                {categorias.length === 0
                    ? <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>Carregando...</div>
                    : <>
                        <Select<VarOpt, false, GroupBase<VarOpt>>
                            options={varOptions}
                            value={selectedVar}
                            onChange={opt => setVarCampo(opt?.value ?? '')}
                            placeholder="Buscar variável..."
                            isClearable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={darkSelectStyles}
                        />
                        <button
                            style={{ ...APPLY_BTN, marginTop: 8, opacity: !varCampo ? 0.4 : 1,
                                background: varAdded ? 'rgba(240,63,63,0.15)' : APPLY_BTN.background,
                                color: varAdded ? '#F03F3F' : '#fff',
                            }}
                            onClick={handleVarAction}
                            disabled={!varCampo}
                        >
                            {varAdded ? '− Remover gráfico' : '+ Adicionar gráfico'}
                        </button>

                        {/* Lista de variáveis adicionadas */}
                        {(() => {
                            const added = existingChartIds
                                .filter(id => id.startsWith('variavel:'))
                                .map(id => {
                                    const campo = id.replace('variavel:', '')
                                    const label = varOptions
                                        .flatMap(g => (g as any).options as VarOpt[])
                                        .find(o => o.value === campo)?.label ?? campo
                                    return { campo, label }
                                })
                            if (added.length === 0) return null
                            return (
                                <div style={{ marginTop: 10 }}>
                                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', fontFamily: 'Nunito,sans-serif', marginBottom: 6 }}>
                                        Adicionados ({added.length})
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        {added.map(({ campo, label }) => (
                                            <div key={campo} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 8px', borderRadius: 8, background: 'rgba(82,146,209,0.1)', fontFamily: 'Nunito,sans-serif', fontSize: 12, color: '#7BB8E8' }}>
                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, marginRight: 6 }}>{label}</span>
                                                <button
                                                    onClick={() => onRemoveChart('variavel', campo)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.35)', fontSize: 14, lineHeight: 1, padding: 0, flexShrink: 0 }}
                                                    title={`Remover ${label}`}
                                                >✕</button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )
                        })()}
                    </>
                }
            </div>

            {/* ── Estatísticas ── */}
            <div style={CARD}>
                <div style={SECTION_TITLE}>Estatísticas</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 5 }}>
                    {STAT_CHIPS.map(stat => {
                        const active = enabledStats.has(stat.key)
                        return (
                            <button
                                key={stat.key}
                                title={stat.label}
                                onClick={() => onToggleStat(stat.key)}
                                style={{
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                    gap: 2, padding: '6px 4px', borderRadius: 8, cursor: 'pointer',
                                    border: `1px solid ${active ? stat.color : 'rgba(255,255,255,0.1)'}`,
                                    background: active ? `${stat.color}18` : 'rgba(255,255,255,0.04)',
                                    transition: 'all 0.15s',
                                }}
                                onMouseEnter={e => { if (active) return; e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)' }}
                                onMouseLeave={e => { if (active) return; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)' }}
                            >
                                <span style={{ fontFamily: 'Montserrat,sans-serif', fontSize: 13, fontWeight: 700, color: active ? stat.color : 'rgba(255,255,255,0.35)' }}>
                                    {stat.symbol}
                                </span>
                                <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 9, color: active ? stat.color : 'rgba(255,255,255,0.3)', letterSpacing: 0.2 }}>
                                    {stat.label}
                                </span>
                            </button>
                        )
                    })}
                </div>
                {enabledStats.size > 0 && (
                    <button
                        onClick={() => enabledStats.forEach(k => onToggleStat(k))}
                        style={{ marginTop: 6, background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'rgba(255,255,255,0.3)', fontFamily: 'Nunito,sans-serif', textAlign: 'right', width: '100%' }}
                    >
                        Limpar todas
                    </button>
                )}
            </div>

            {/* ── Escala do Eixo Y ── */}
            <div style={CARD}>
                <div style={SECTION_TITLE}>Escala do Eixo Y</div>

                {/* Nível 1: Auto | Manual */}
                <div style={{ display: 'flex', gap: 4, marginBottom: axisMode !== 'manual' ? 8 : 10 }}>
                    {([['auto', 'Automático'], ['manual', 'Manual']] as const).map(([key, label]) => {
                        const active = key === 'auto' ? axisMode !== 'manual' : axisMode === 'manual'
                        return (
                            <button
                                key={key}
                                onClick={() => onAxisModeChange(key === 'auto' ? 'moderado' : 'manual')}
                                style={{
                                    flex: 1, padding: '6px 0', borderRadius: 8, cursor: 'pointer',
                                    border: `1px solid ${active ? '#5292D1' : 'rgba(255,255,255,0.1)'}`,
                                    background: active ? 'rgba(82,146,209,0.15)' : 'rgba(255,255,255,0.04)',
                                    color: active ? '#5292D1' : 'rgba(255,255,255,0.4)',
                                    fontFamily: 'Montserrat,sans-serif', fontSize: 11, fontWeight: 700,
                                    textTransform: 'uppercase' as const, letterSpacing: 0.5,
                                    transition: 'all 0.15s',
                                }}
                                onMouseEnter={e => { if (active) return; e.currentTarget.style.background = 'rgba(82,146,209,0.08)'; e.currentTarget.style.borderColor = 'rgba(82,146,209,0.4)'; e.currentTarget.style.color = '#5292D1' }}
                                onMouseLeave={e => { if (active) return; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = 'rgba(255,255,255,0.4)' }}
                            >
                                {label}
                            </button>
                        )
                    })}
                </div>

                {/* Nível 2: sub-modos do automático */}
                {axisMode !== 'manual' && (
                    <div style={{ display: 'flex', gap: 4, marginBottom: 2 }}>
                        {([
                            ['amplo',    'Amplo',    'Maior margem — bom para contexto geral'],
                            ['moderado', 'Moderado', 'Margem equilibrada'],
                            ['preciso',  'Preciso',  'Mínima margem — destaca oscilações'],
                        ] as const).map(([key, label, tip]) => {
                            const active = axisMode === key
                            return (
                                <button
                                    key={key}
                                    title={tip}
                                    onClick={() => onAxisModeChange(key)}
                                    style={{
                                        flex: 1, padding: '5px 0', borderRadius: 7, cursor: 'pointer',
                                        border: `1px solid ${active ? '#92B258' : 'rgba(255,255,255,0.08)'}`,
                                        background: active ? 'rgba(146,178,88,0.15)' : 'rgba(255,255,255,0.03)',
                                        color: active ? '#92B258' : 'rgba(255,255,255,0.35)',
                                        fontFamily: 'Nunito,sans-serif', fontSize: 11, fontWeight: active ? 700 : 400,
                                        transition: 'all 0.15s',
                                    }}
                                    onMouseEnter={e => { if (active) return; e.currentTarget.style.background = 'rgba(146,178,88,0.08)'; e.currentTarget.style.borderColor = 'rgba(146,178,88,0.35)'; e.currentTarget.style.color = '#92B258' }}
                                    onMouseLeave={e => { if (active) return; e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.35)' }}
                                >
                                    {label}
                                </button>
                            )
                        })}
                    </div>
                )}

                {/* Manual: inputs por gráfico */}
                {axisMode === 'manual' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {Object.keys(chartLabels).length === 0 ? (
                            <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, fontFamily: 'Nunito,sans-serif' }}>
                                Nenhum gráfico adicionado
                            </div>
                        ) : (
                            Object.entries(chartLabels).map(([id, label]) => (
                                <div key={id}>
                                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', fontFamily: 'Nunito,sans-serif', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={label}>
                                        {label}
                                    </div>
                                    <div style={{ display: 'flex', gap: 5 }}>
                                        <input
                                            type="number"
                                            placeholder="Min"
                                            value={axisConfig[id]?.min ?? ''}
                                            onChange={e => onAxisConfigChange(id, 'min', e.target.value)}
                                            style={SCALE_INPUT}
                                        />
                                        <input
                                            type="number"
                                            placeholder="Max"
                                            value={axisConfig[id]?.max ?? ''}
                                            onChange={e => onAxisConfigChange(id, 'max', e.target.value)}
                                            style={SCALE_INPUT}
                                        />
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>

            {/* ── Período ── */}
            <div style={CARD}>
                <div style={SECTION_TITLE}>Período</div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
                    {PERIOD_PRESETS.map(p => {
                        // Período aplicado (inicio/fim, não o pendente no Flatpickr) bate com a duração
                        // da predefinição — tolerância de 1s só por segurança, não deveria nunca divergir.
                        const isActive = Math.abs((fim.getTime() - inicio.getTime()) - p.ms) < 1000
                        return (
                            <button
                                key={p.label}
                                onClick={() => applyPreset(p.ms)}
                                style={{
                                    flex: '1 0 auto', minWidth: 36, padding: '5px 0', borderRadius: 7,
                                    border: `1px solid ${isActive ? '#5292D1' : 'rgba(255,255,255,0.12)'}`,
                                    background: isActive ? 'rgba(82,146,209,0.25)' : 'rgba(255,255,255,0.05)',
                                    color: isActive ? '#fff' : 'rgba(255,255,255,0.6)', fontFamily: 'Nunito,sans-serif', fontSize: 11,
                                    fontWeight: isActive ? 700 : 600, cursor: 'pointer', transition: 'all 0.15s',
                                }}
                                onMouseEnter={e => { if (isActive) return; e.currentTarget.style.background = 'rgba(82,146,209,0.15)'; e.currentTarget.style.borderColor = '#5292D1'; e.currentTarget.style.color = '#5292D1' }}
                                onMouseLeave={e => { if (isActive) return; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)' }}
                            >
                                {p.label}
                            </button>
                        )
                    })}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 4, fontFamily: 'Nunito,sans-serif', letterSpacing: 0.3 }}>
                            INÍCIO
                        </div>
                        <div className="dhm-fp-wrap">
                            <Flatpickr
                                ref={fpInicioRef}
                                onChange={([d]) => d && setLocalInicio(d)}
                                options={inicioFpOptions}
                                placeholder="dd/mm/aaaa hh:mm"
                            />
                        </div>
                    </div>

                    <div>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 4, fontFamily: 'Nunito,sans-serif', letterSpacing: 0.3 }}>
                            FIM
                        </div>
                        <div className="dhm-fp-wrap">
                            <Flatpickr
                                ref={fpFimRef}
                                onChange={([d]) => d && setLocalFim(d)}
                                options={fimFpOptions}
                                placeholder="dd/mm/aaaa hh:mm"
                            />
                        </div>
                    </div>
                </div>

                <button style={APPLY_BTN} onClick={() => onDateChange(localInicio, localFim)}>
                    Aplicar período
                </button>
            </div>

        </div>
    )
}

export default Sidebar
